import {buildPreparationLabelData} from '@/modules/preparation-label/build-label-data'
import {getLabelCaptions, renderLabelAssets} from '@/modules/preparation-label/label-assets'
import {mapLabelSource, toLabelSettings} from '@/modules/preparation-label/map-label-source'
import type {LabelTranslate} from '@/modules/preparation-label/types'
import {buildPreparationReportData} from './build-report-data'
import {getPreviewVolumes, mapReportSource} from './map-report-source'
import type {ReportTranslate} from './types'
import {useGetSettings} from '@/modules/settings/api'

type LabelSource = Parameters<typeof mapLabelSource>[0]['source']
type StatusLogs = Parameters<typeof mapLabelSource>[0]['statusLogs']
type Sequence = Parameters<typeof mapReportSource>[0]['sequence']
/** the settings row the drawer and the Reports list both read */
type Settings = NonNullable<ReturnType<typeof useGetSettings>['settings']>

/**
 * The production report of one prescription, including its label copy when the report carries one.
 * Shared by the side drawer and the Reports list, so a reprint is the very same document (CY-87).
 */
export async function buildReportData(input: {
  source: LabelSource
  statusLogs: StatusLogs
  sequence: Sequence
  settings: Settings
  labelTranslate: LabelTranslate
  reportTranslate: ReportTranslate
}) {
  const labelInput = mapLabelSource({
    source: input.source,
    statusLogs: input.statusLogs,
    settings: toLabelSettings(input.settings),
    volumes: getPreviewVolumes(input.sequence),
  })
  const label = buildPreparationLabelData(labelInput, input.labelTranslate)
  const report = buildPreparationReportData(
    mapReportSource({
      labelInput,
      source: input.source,
      sequence: input.sequence,
      statusLogs: input.statusLogs,
      settings: input.settings,
      reportDate: Date.now(),
    }),
    label,
    input.reportTranslate
  )

  const labelCopy = report.showLabelCopy
    ? {label, ...(await renderLabelAssets(label)), captions: getLabelCaptions(key => input.labelTranslate(key))}
    : null

  return {report, labelCopy}
}
